/**
 * Anime cover cache + resolver for the music UI.
 *
 * Karaoke Mugen doesn't expose per-kara cover art — but every kara
 * carries `anilist_ids` (and sometimes `myanimelist_ids`) plus a series
 * name. We use those to pull the matching anime's poster/banner/accent
 * out of Yozora and surface it in the music chrome (cards, queue rows,
 * immersive hero) so the visuals match the rest of the app.
 *
 * Lookups are deduped + cached at module scope. A single in-flight
 * request is shared between concurrent callers asking for the same key
 * so a freshly-rendered shelf doesn't fan out twelve identical fetches.
 */

export interface AnimeCover {
  animeId: number | null;
  title: string | null;
  poster: string | null;
  banner: string | null;
  /** Hex like `#aabbcc` when Yozora has a dominant color, otherwise null. */
  accentColor: string | null;
}

type Resolution = AnimeCover | null;

const byId = new Map<number, Resolution>();
const byName = new Map<string, Resolution>();
const inflightById = new Map<number, Promise<Resolution>>();
const inflightByName = new Map<string, Promise<Resolution>>();

/**
 * localStorage persistence — covers never change, so we save them
 * across sessions. Cap the cache to keep storage usage bounded.
 *
 * Schema:
 *   `anime-cover-cache-v1` → JSON { byId: {[id]: cover}, byName: {[norm]: cover} }
 */
const STORAGE_KEY = 'anime-cover-cache-v1';
const MAX_ENTRIES = 500;
let saveTimer: number | null = null;

function loadFromStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      byId?: Record<string, AnimeCover>;
      byName?: Record<string, AnimeCover>;
    };
    if (parsed.byId) {
      Object.entries(parsed.byId).forEach(([id, cover]) => {
        const numId = Number.parseInt(id, 10);
        if (Number.isFinite(numId)) byId.set(numId, cover);
      });
    }
    if (parsed.byName) {
      Object.entries(parsed.byName).forEach(([name, cover]) => {
        byName.set(name, cover);
      });
    }
  } catch {
    // ignore — corrupt storage is non-fatal, just slower
  }
}

function scheduleSave(): void {
  if (typeof window === 'undefined') return;
  if (saveTimer !== null) return;
  // Coalesce writes — many cover lookups can resolve in the same tick
  // when a shelf renders, so we batch to a single localStorage round-
  // trip.
  saveTimer = window.setTimeout(() => {
    saveTimer = null;
    try {
      // Cap the cache before serialising. Take the most-recently-written
      // entries (Map preserves insertion order in modern engines).
      const idEntries = [...byId.entries()].slice(-MAX_ENTRIES);
      const nameEntries = [...byName.entries()].slice(-MAX_ENTRIES);
      const payload = {
        byId: Object.fromEntries(idEntries),
        byName: Object.fromEntries(nameEntries),
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Quota errors / storage disabled — non-fatal
    }
  }, 800);
}

// Hydrate from localStorage immediately on module evaluation so the
// first render of any card has the cache available.
loadFromStorage();

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replaceAll(/\s+/g, ' ');
}

type YozoraPoster = {
  small?: string | null;
  medium?: string | null;
  large?: string | null;
};

type YozoraAnime = {
  id?: number;
  title?: {
    english?: string | null;
    romaji?: string | null;
    native?: string | null;
  };
  poster?: YozoraPoster | null;
  background?: string | null;
  color?: string | null;
};

function pickPoster(anime: YozoraAnime): string | null {
  return (
    anime.poster?.large ||
    anime.poster?.medium ||
    anime.poster?.small ||
    null
  );
}

function pickTitle(anime: YozoraAnime): string | null {
  return (
    anime.title?.english?.trim() ||
    anime.title?.romaji?.trim() ||
    anime.title?.native?.trim() ||
    null
  );
}

function toCover(anime: YozoraAnime): AnimeCover | null {
  if (!anime) return null;
  const poster = pickPoster(anime);
  // If neither poster nor banner is present, the lookup hit something
  // unusable. Returning null lets the consumer fall back to the kind
  // gradient instead of rendering an empty image.
  if (!poster && !anime.background) return null;
  return {
    animeId: typeof anime.id === 'number' ? anime.id : null,
    title: pickTitle(anime),
    poster,
    banner: anime.background ?? null,
    accentColor: anime.color ?? null,
  };
}

async function fetchById(animeId: number): Promise<Resolution> {
  if (byId.has(animeId)) return byId.get(animeId) ?? null;
  const cached = inflightById.get(animeId);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const res = await fetch(`/api/anime/${animeId}`);
      if (!res.ok) return null;
      const body = (await res.json()) as YozoraAnime;
      return toCover(body);
    } catch {
      return null;
    }
  })()
    .then((value) => {
      byId.set(animeId, value);
      inflightById.delete(animeId);
      if (value?.title) {
        byName.set(normalizeName(value.title), value);
      }
      scheduleSave();
      return value;
    })
    .catch(() => {
      inflightById.delete(animeId);
      return null;
    });

  inflightById.set(animeId, promise);
  return promise;
}

async function fetchByName(name: string): Promise<Resolution> {
  const key = normalizeName(name);
  if (!key) return null;
  if (byName.has(key)) return byName.get(key) ?? null;
  const cached = inflightByName.get(key);
  if (cached) return cached;

  const promise = (async () => {
    try {
      const params = new URLSearchParams({
        search: name,
        perPage: '1',
        sort: 'POPULARITY_DESC',
      });
      const res = await fetch(`/api/animes?${params.toString()}`);
      if (!res.ok) return null;
      const body = (await res.json()) as { data?: YozoraAnime[] };
      const first = body.data?.[0];
      if (!first) return null;
      const cover = toCover(first);
      if (cover?.animeId) byId.set(cover.animeId, cover);
      return cover;
    } catch {
      return null;
    }
  })()
    .then((value) => {
      byName.set(key, value);
      inflightByName.delete(key);
      scheduleSave();
      return value;
    })
    .catch(() => {
      inflightByName.delete(key);
      return null;
    });

  inflightByName.set(key, promise);
  return promise;
}

/**
 * Resolve a cover for a kara given its AniList id (when present) and
 * series name. Falls through id → name search → null. Always cached.
 */
export async function resolveAnimeCover(
  animeId: number | null | undefined,
  animeName: string | null | undefined
): Promise<AnimeCover | null> {
  if (typeof animeId === 'number' && Number.isFinite(animeId) && animeId > 0) {
    const direct = await fetchById(animeId);
    if (direct) return direct;
  }
  if (animeName && animeName.trim().length > 0 && animeName !== 'Unknown') {
    return fetchByName(animeName);
  }
  return null;
}

/**
 * Synchronous cache peek — returns whatever is already resolved without
 * triggering a fetch. Useful as the initial state for the React hook so
 * first paint can show a cover when the cache is warm.
 */
export function peekAnimeCover(
  animeId: number | null | undefined,
  animeName: string | null | undefined
): AnimeCover | null {
  if (typeof animeId === 'number' && byId.has(animeId)) {
    return byId.get(animeId) ?? null;
  }
  if (animeName) {
    const key = normalizeName(animeName);
    if (byName.has(key)) return byName.get(key) ?? null;
  }
  return null;
}
